'use client';

import React, { useEffect, useState, useRef } from 'react';
import { useFormat } from 'helpers/hooks/useFormat';
import useLocale from 'helpers/hooks/useLocale';
import { getCountryFromCode } from 'project.config';
import { PickupPoint } from 'components/gruenland/organisms/checkout/ct-checkout/components/ShippingSelection';
import { useCart } from 'frontastic';
import useShippyProMapsApiKey from './useShippyProMapsApiKey';

interface ShippyProPoint {
  id: string;
  title: string;
  address: string;
  courier: string;
  type: string | false;
  city: string;
  country: string;
  state: string;
  zip: string;
  street: string;
  lat: number;
  lon: number;
  distance: string;
  distanceCar: string;
  durationCar: string;
  phone: string | undefined;
  email: string | undefined;
  img: string;
}

type Props = {
  onSelectPoint: (point: PickupPoint) => void;
  onBack: () => void;
};

export const PickupPointSelector: React.FC<Props> = ({ onSelectPoint, onBack }) => {
  const { formatMessage } = useFormat({ name: 'gruenland' });
  const [selectedPoint, setSelectedPoint] = useState<PickupPoint | null>(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const locale = useLocale();
  const mapsApiKey = useShippyProMapsApiKey();
  const { data: cart } = useCart();
  const cartCountry = cart?.country;
  const callbackName = useRef(`__shippypro_cb_${Math.random().toString(36).slice(2)}`);

  const countryMismatch =
    !!selectedPoint && !!cartCountry && selectedPoint.country?.toUpperCase() !== cartCountry.toUpperCase();

  useEffect(() => {
    if (!containerRef.current || !mapsApiKey) return;
    if (document.querySelector('script[src*="shippypro.com/maps"]')) {
      setIsScriptLoaded(true);
      return;
    }
    const mapContainer = document.getElementById('shippypro-map-container');
    if (!mapContainer) return;

    (window as Record<string, unknown>)[callbackName.current] = (data?: ShippyProPoint) => {
      if (data?.id && data?.title) {
        setSelectedPoint({
          id: data.id,
          name: data.title,
          address: data.address,
          courier: data.courier,
          type: data.type ? String(data.type) : '',
          city: data.city,
          country: data.country,
          zip: data.zip,
          street: data.street,
          state: data.state,
        });
      }
    };

    const script = document.createElement('script');
    script.src = 'https://www.shippypro.com/maps/mapsDropOffPoints.js';
    script.integrity = 'sha256-OFf190WzV7Hd52QFVVT6+qdSgKDTmSmZpYLFQgUIAO4=';
    script.crossOrigin = 'anonymous';
    script.setAttribute('key', mapsApiKey);
    script.setAttribute('lang', locale);
    script.setAttribute('maps', '');
    script.setAttribute('callback', callbackName.current);

    script.onload = () => setIsScriptLoaded(true);
    script.onerror = () => setError(new Error('Errore nel caricamento dello script ShippyPro'));

    mapContainer.appendChild(script);

    const name = callbackName.current;
    return () => {
      script.parentNode?.removeChild(script);
      delete (window as Record<string, unknown>)[name];
    };
  }, [mapsApiKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirm = () => {
    if (selectedPoint && !countryMismatch) {
      onSelectPoint(selectedPoint);
    }
  };

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600">{error.message}</p>
        <button onClick={onBack} className="mt-4 rounded bg-gray-800 px-6 py-2 text-white hover:bg-gray-700">
          {formatMessage({ id: 'common.back', defaultMessage: 'Indietro' })}
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-8">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">
          {formatMessage({
            id: 'shipping.selectPickupPoint',
            defaultMessage: 'Seleziona un punto di ritiro',
          })}
        </h2>
        <button onClick={onBack} className="text-gray-600 hover:text-gray-900">
          ← {formatMessage({ id: 'common.back', defaultMessage: 'Indietro' })}
        </button>
      </div>

      {/* Container for ShippyPro map */}
      <div ref={containerRef} className="rounded-lg border border-gray-200 bg-white p-4">
        {/* eslint-disable-next-line tailwindcss/no-custom-classname */}
        <div id="shippypro-map-container">
          {!isScriptLoaded && (
            <div className="flex h-[400px] items-center justify-center">
              <p className="text-gray-600">
                {formatMessage({ id: 'common.loading', defaultMessage: 'Caricamento...' })}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Selected point info */}
      {selectedPoint && !countryMismatch && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4">
          <h3 className="mb-2 font-semibold text-green-900">
            {formatMessage({ id: 'shipping.selectedPoint', defaultMessage: 'Punto selezionato' })}
          </h3>
          <div className="space-y-1 text-sm text-green-800">
            <p className="font-medium">{selectedPoint.name}</p>
            <p>{selectedPoint.address}</p>
            {selectedPoint.courier && <p>Corriere: {selectedPoint.courier}</p>}
          </div>
        </div>
      )}

      {/* Country mismatch warning */}
      {countryMismatch && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-medium text-red-700">
            {formatMessage({
              id: 'shipping.pickupCountryMismatch',
              defaultMessage: 'Il punto di ritiro deve trovarsi in {country}. Selezionane un altro.',
              values: { country: getCountryFromCode(cartCountry ?? '')?.countryName ?? cartCountry ?? '' },
            })}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-4">
        <button
          onClick={onBack}
          className="rounded border border-gray-300 bg-white px-6 py-2 text-gray-700 hover:bg-gray-50"
        >
          {formatMessage({ id: 'common.cancel', defaultMessage: 'Annulla' })}
        </button>
        <button
          onClick={handleConfirm}
          disabled={!selectedPoint || countryMismatch}
          className="rounded bg-gray-900 px-6 py-2 text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-300"
        >
          {formatMessage({ id: 'common.confirm', defaultMessage: 'Conferma' })}
        </button>
      </div>
    </div>
  );
};
